import { useMemo, useState } from 'react'

import type { MapRef } from 'react-map-gl/mapbox'

import type { DropdownOption } from '@/components/ui/dropdown/Dropdown'
import { useCompaniesForSource, useDashboard } from '@/hooks'
import { companyLabelWithHierarchy } from '@/utils/companyHierarchy'

import { defaultSidebarState, useMapContext, type SaveableAssetMapContext } from '../MapContext'
import { useStatusColumnsContext } from '../StatusColumns'
import { AssetStateSummary } from '../types'
import { getCenterFromBounds, getUSBounds, getResponsiveUSZoom } from '../utils'

export const useMapControls = (mapRef: React.RefObject<MapRef | null>) => {
  const context = useMapContext()
  // The map's own picker, so the company list has to come from the back end the map is
  // reading — a Fresh company chosen while showing Timescale assets is the mix we avoid.
  const { companies, isLoading: isLoadingCompanies } = useCompaniesForSource(context.dataSource)
  const { selectedSavedMap, setSelectedSavedMap } = context
  const { configuration, saveAssetMap, deleteAssetMap } = useDashboard()
  const { statusColumns } = useStatusColumnsContext()

  const [showSaveModal, setShowSaveModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [saveMapName, setSaveMapName] = useState('')
  const [saveMapIsDefault, setSaveMapIsDefault] = useState(false)
  const [saveError, setSaveError] = useState('')

  // Saved maps dropdown options
  const savedMapOptions: DropdownOption[] = useMemo(() => {
    if (!configuration.assetMaps) return []
    return Object.keys(configuration.assetMaps).map((name) => ({
      value: name,
      label: name,
    }))
  }, [configuration?.assetMaps])

  const selectedSavedMapOption = useMemo(() => {
    if (!selectedSavedMap) return null
    return savedMapOptions.find((option) => option.value === selectedSavedMap.name) || null
  }, [selectedSavedMap, savedMapOptions])

  // Company dropdown options
  const companyOptions: DropdownOption[] = useMemo(() => {
    return companies.map((company) => ({
      value: company.id.toString(),
      label: company.name,
      fullName: company.fullName ?? companyLabelWithHierarchy(company, companies),
    }))
  }, [companies])

  const selectedCompanyOption = useMemo(() => {
    const { companyId } = context.filters
    if (!companyId) return null
    return companyOptions.find((option) => option.value === companyId.toString()) || null
  }, [context.filters.companyId, companyOptions])

  const handleCompanyChange = (option: DropdownOption | null) => {
    const companyId = option ? parseInt(option.value) : undefined
    context.setFilters({ ...context.filters, companyId })
    setSelectedSavedMap(null)
  }

  const handleSavedMapChange = (option: DropdownOption | null) => {
    if (!option) {
      setSelectedSavedMap(null)
      return
    }
    const savedMap = configuration.assetMaps?.[option.value]
    if (savedMap) {
      if (savedMap.companyId && !companies.some((c) => c.id === savedMap.companyId)) {
        return
      }
      context.setIsSwitchingView(true)
      setSelectedSavedMap(savedMap)
      context.setSidebarState({ ...defaultSidebarState, ...savedMap.sidebarState })
      context.setMapState(savedMap.mapState)
      context.setFilters({
        companyId: savedMap.companyId,
        ...savedMap.filters,
        alarmCodes: new Set(savedMap.filters.alarmCodes as unknown as number[]),
        alerts: new Set(savedMap.filters.alerts as unknown as string[]),
      })
      const map = mapRef.current?.getMap()
      if (map && savedMap.mapState.bounds) {
        const { north, south, east, west } = savedMap.mapState.bounds
        map.fitBounds(
          [
            [west, south],
            [east, north],
          ],
          { duration: 400 }
        )
      }
      setTimeout(() => context.setIsSwitchingView(false), 500)
    }
  }

  const handleReset = () => {
    context.reset()
    setSelectedSavedMap(null)
    const map = mapRef.current?.getMap()
    if (map) {
      const defaultBounds = getUSBounds(6)
      const center = getCenterFromBounds(defaultBounds)
      map.flyTo({
        center: [center.longitude, center.latitude],
        zoom: getResponsiveUSZoom(),
        duration: 1000,
      })
    }
  }

  const handleSaveCurrentMap = () => {
    if (selectedSavedMap) {
      setSaveMapName(selectedSavedMap.name)
      setSaveMapIsDefault(selectedSavedMap?.isDefault || false)
    } else {
      setSaveMapName('')
      setSaveMapIsDefault(false)
    }
    setSaveError('')
    setShowSaveModal(true)
  }

  /** The current map, filters and columns, as a saved view called `name`. */
  const buildSavedMap = (name: string, isDefault: boolean): SaveableAssetMapContext => ({
    name,
    companyId: context.filters.companyId || 0,
    sidebarState: context.sidebarState,
    mapState: context.mapState,
    filters: {
      ...context.filters,
      alarmCodes: Array.from(context.filters.alarmCodes) as unknown as Set<number>,
      alerts: Array.from(context.filters.alerts) as unknown as Set<string>,
    },
    isDefault,
    columns: statusColumns.map((c) => ({
      selectedColumnsDatabaseName: c.selectedColumnsDatabaseName,
      selected: c.selected,
      displayOrder: c.displayOrder,
      width: c.width,
    })),
  })

  /** Stores `map` and makes it the only view that opens by default when it is flagged so. */
  const storeSavedMap = (map: SaveableAssetMapContext) => {
    const others = Object.values(configuration.assetMaps ?? {}).filter(
      (m) => m.name !== map.name && m.isDefault && map.isDefault
    )
    others.forEach((m) => saveAssetMap({ ...m, isDefault: false }))
    saveAssetMap(map)
    setSelectedSavedMap(map)
  }

  /** Saves the current state as a new view. Returns an error message, or null once saved. */
  const saveAsNewMap = (name: string, isDefault: boolean): string | null => {
    const trimmedName = name.trim()
    if (!trimmedName) return 'Please enter a name'
    if (configuration.assetMaps?.[trimmedName]) return 'A saved view with this name already exists'
    storeSavedMap(buildSavedMap(trimmedName, isDefault))
    return null
  }

  /** Overwrites the selected view with the current state. */
  const saveSelectedMap = () => {
    if (selectedSavedMap) storeSavedMap(buildSavedMap(selectedSavedMap.name, Boolean(selectedSavedMap.isDefault)))
  }

  /** Changes only whether the selected view opens by default, not what it stores. */
  const setSelectedMapIsDefault = (isDefault: boolean) => {
    if (selectedSavedMap) storeSavedMap({ ...selectedSavedMap, isDefault })
  }

  /** Deletes the selected view. The map keeps its current state, now as no view. */
  const deleteSelectedMap = () => {
    if (selectedSavedMap) {
      deleteAssetMap(selectedSavedMap.name)
      setSelectedSavedMap(null)
    }
  }

  const handleConfirmSave = () => {
    const trimmedName = saveMapName.trim()
    if (!trimmedName) {
      setSaveError('Please enter a name')
      return
    }
    const isOverwriting = selectedSavedMap && trimmedName === selectedSavedMap?.name
    if (!isOverwriting && configuration.assetMaps?.[trimmedName]) {
      setSaveError('A saved map with this name already exists')
      return
    }
    storeSavedMap(buildSavedMap(trimmedName, saveMapIsDefault))
    setShowSaveModal(false)
  }

  const handleDeleteSavedMap = () => {
    if (selectedSavedMap) {
      setShowDeleteModal(true)
    }
  }

  const confirmDeleteSavedMap = () => {
    if (selectedSavedMap) {
      deleteAssetMap(selectedSavedMap.name)
      setSelectedSavedMap(null)
      setShowDeleteModal(false)
    }
  }

  return {
    companies,
    isLoadingCompanies,
    savedMapOptions,
    selectedSavedMapOption,
    companyOptions,
    selectedCompanyOption,
    showSaveModal,
    setShowSaveModal,
    showDeleteModal,
    setShowDeleteModal,
    saveMapName,
    setSaveMapName,
    saveMapIsDefault,
    setSaveMapIsDefault,
    saveError,
    setSaveError,
    handleCompanyChange,
    handleSavedMapChange,
    handleReset,
    handleSaveCurrentMap,
    handleConfirmSave,
    saveAsNewMap,
    saveSelectedMap,
    setSelectedMapIsDefault,
    deleteSelectedMap,
    handleDeleteSavedMap,
    confirmDeleteSavedMap,
  }
}

export const useAssetList = (
  mapRef: React.RefObject<MapRef | null>,
  setCurrentAssetId: (id: number | undefined, origin: string) => void
) => {
  const context = useMapContext()

  const assets = context.sidebarState.limitToMap ? context.viewportFilteredAssets : context.filteredAssets

  const handleAssetClick = (asset: AssetStateSummary) => {
    setCurrentAssetId(asset.id, 'list')
    const map = mapRef.current?.getMap()
    if (map) {
      map.flyTo({
        center: [asset.location.longitude, asset.location.latitude],
        duration: 1000,
      })
    }
  }

  return {
    assets,
    context,
    handleAssetClick,
  }
}

export type UseMapControlsReturn = ReturnType<typeof useMapControls>
